import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { Base } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { EditItem, IEditItem } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/editItem";
import { FileAttachment, IFileAttachment } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/fileAttachment";
import { Translations } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/translations";
import { IModifyUser, ModifyUser } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/user/modifyUser";
import { EventEditItemMessage, IEventEditItemMessage } from "ui/Scripts/source/models/event/eventEditItemMessage";

export interface IEventEditItem extends IEditItem {
    companyId: string;
    unitId: string;
    unitCode: string;
    createCompanyId: string;
    createCompanyUnitId: string;
    createCompanyUnitCode: string;
    code: string;
    urgencyClassId: string;
    date: number;
    name: string;
    observer: string;
    observerAction: string;
    description: string;
    locationName: string;
    latitude: number;
    longitude: number;
    immediateAction: string;
    preventiveAction: string;
    rootCause: string;
    identificationDetails: string;
    directCosts: number;
    indirectCosts: number;
    amount: number;
    isRead: boolean;
    attachments: IFileAttachment[];
    createUser: IModifyUser;
    modifyUser: IModifyUser;
    publishUser: IModifyUser;
    lockedUser: IModifyUser;
    handlers: IModifyUser[];
    messages: IEventEditItemMessage[];
    eventTypeIds: string[];
    eventTypeBIds: string[];
    eventTypeCIds: string[];

    stateStr: string;

    isPublished(): boolean;

    isLocked(): boolean;
    isLockedToUser(userCode: string): boolean;
    isLockedToOtherUser(userCode: string): boolean;
}

export class EventEditItem extends EditItem implements IEventEditItem {
    companyId: string;
    unitId: string;
    unitCode: string;
    createCompanyId: string;
    createCompanyUnitId: string;
    createCompanyUnitCode: string;
    code: string;
    urgencyClassId: string;
    date: number;
    name: string;
    observer: string;
    observerAction: string;
    description: string;
    locationName: string;
    latitude: number;
    longitude: number;
    immediateAction: string;
    preventiveAction: string;
    rootCause: string;
    identificationDetails: string;
    directCosts: number;
    indirectCosts: number;
    amount: number;
    isRead: boolean;
    attachments: IFileAttachment[];
    createUser: IModifyUser;
    modifyUser: IModifyUser;
    publishUser: IModifyUser;
    lockedUser: IModifyUser;
    handlers: ModifyUser[];
    messages: EventEditItemMessage[];
    eventTypeIds: string[];
    eventTypeBIds: string[];
    eventTypeCIds: string[];
    
    stateStr: string;

    constructor();
    constructor(obj: IEventEditItem);
    constructor(obj?: any) {
        super(obj);
        this.companyId = obj && obj.companyId || "";
        this.unitId = obj && obj.unitId || "";
        this.unitCode = obj && obj.unitCode || "";
        this.createCompanyId = obj && obj.createCompanyId || "";
        this.createCompanyUnitId = obj && obj.createCompanyUnitId || "";
        this.createCompanyUnitCode = obj && obj.createCompanyUnitCode || "";
        this.code = obj && obj.code || "";
        this.urgencyClassId = obj && obj.urgencyClassId || "";
        this.date = obj && obj.date || 0;
        this.name = obj && obj.name || "";
        this.observer = obj && obj.observer || "";
        this.observerAction = obj && obj.observerAction || "";
        this.description = obj && obj.description || "";
        this.locationName = obj && obj.locationName || "";
        this.latitude = obj && obj.latitude || 0;
        this.longitude = obj && obj.longitude || 0;
        this.immediateAction = obj && obj.immediateAction || "";
        this.preventiveAction = obj && obj.preventiveAction || "";
        this.rootCause = obj && obj.rootCause || "";
        this.identificationDetails = obj && obj.identificationDetails || "";
        this.directCosts = obj && obj.directCosts || 0;
        this.indirectCosts = obj && obj.indirectCosts || 0;
        this.amount = obj && obj.amount || 0;
        this.isRead = obj && obj.isRead || false;
        this.createUser = new ModifyUser(obj?.createUser);
        this.modifyUser = new ModifyUser(obj?.modifyUser);
        this.publishUser = obj?.publishUser
            ? new ModifyUser(obj.publishUser)
            : null;
        this.lockedUser = obj?.lockedUser
            ? new ModifyUser(obj.lockedUser)
            : null;
        this.attachments = Base.getTypedArray(obj ? obj.attachments : null, FileAttachment);
        FileAttachment.sortFileAttachments(this.attachments);
        this.handlers = ModifyUser.sortModifyUsers(Base.getTypedArray(obj?.handlers, ModifyUser));
        this.messages = EventEditItemMessage.sortEventEditItemMessages(Base.getTypedArray(obj?.messages, EventEditItemMessage));
        this.eventTypeIds = obj?.eventTypeIds ?? [];
        this.eventTypeBIds = obj?.eventTypeBIds ?? [];
        this.eventTypeCIds = obj?.eventTypeCIds ?? [];
        //Set state string
        const approveHandlers = EventEditItem.getFirstAndLastApproveHandler(this.handlers);
        this.stateStr = approveHandlers.lastApprove
            ? Translations.Closed
            : (!this.publishUser
                ? Translations.Draft
                : (this.handlers.length < 1
                    ? Translations.NotAssigned
                    : Translations.StateOpen));
    }

    isPublished(): boolean {
        return !!this.publishUser;
    }

    isLocked(): boolean {
        return !!this.lockedUser;
    }

    isLockedToUser(userCode: string): boolean {
        return LockedRecord.isLockedToUser(this.lockedUser, userCode);
    }

    isLockedToOtherUser(userCode: string): boolean {
        return this.lockedUser?.code && !userCode.equalIgnoreCase(this.lockedUser.code);
    }

    static getFirstAndLastApproveHandler(handlers: IModifyUser[]): { firstApprove: IModifyUser, lastApprove: IModifyUser } {
        const result = {
            firstApprove: null,
            lastApprove: null
        };
        let allApproved = true;
        for (const handler of handlers) {
            if (!handler.date) {
                allApproved = false;
                result.lastApprove = null;
                continue;
            }
            if (!result.firstApprove || result.firstApprove.date > handler.date) {
                result.firstApprove = handler;
            }
            if (allApproved && (!result.lastApprove || result.lastApprove.date < handler.date)) {
                result.lastApprove = handler;
            }
        }
        return result;
    }

    static getProcessingTimeStr(lastApproveUser: IModifyUser, publishUser: IModifyUser, createUser: IModifyUser): string {
        const processingTime = lastApproveUser ? lastApproveUser.date - (publishUser ? publishUser.date : createUser.date) : null;
        if (!processingTime) return "";
        let hours = processingTime / (60 * 60 * 1000);
        const days = Math.floor(hours / 24);
        hours = hours % 24;
        return (days < 0.5
            ? ""
            : days.toLocaleFixed(0) + " " + Translations.Days + " ") + hours.toLocaleFixed(0) + " " + Translations.Hours;
    }
}